import { UserFlags } from '@ordermentum/ui-types';
import qs from 'qs';
import useSWRImmutable from 'swr/immutable';
import { FLAGS_URL } from '@ordermentum/ui-constants';
import { SWRResponse } from 'swr';
import { fetcher } from '../../swr/fetchers';
import { HookOptionsTypes } from '../../types/HookOptionsTypes';

type UseFlagChecksParams = {
  flags: string[];
  context?: {
    userId: string;
    source?: string;
    retailerId?: string;
    supplierId?: string;
  };
};

type FlagCheckResponse = SWRResponse<UserFlags>;

/**
 * Fetches feature flags for a user/context (SWR-based, preferred).
 *
 * Feature flags control feature visibility and behavior. Used throughout admin for conditional rendering and feature toggles.
 * This is the preferred hook (SWR-based) over the legacy Redux `useFlags`.
 *
 * @param params - Object containing flags array and optional context
 * @param params.flags - Array of flag names to check (required)
 * @param params.context - Optional context for flag evaluation
 * @param params.context.userId - Required if context is provided
 * @param params.context.supplierId - Optional supplier context
 * @param params.context.retailerId - Optional retailer context
 * @param params.context.source - Optional source identifier
 * @param options - Optional SWR hook options
 * @param options.skip - Skip fetching if true
 * @returns SWR response with UserFlags object (flag names as keys, boolean values)
 *
 * @example
 * ```tsx
 * const { data: flags, isLoading } = useFlagCheck(
 *   {
 *     flags: ['feature-flag-1', 'feature-flag-2'],
 *     context: {
 *       userId: '123',
 *       supplierId: '456',
 *       retailerId: '789',
 *       source: 'admin-app'
 *     }
 *   },
 *   { skip: false }
 * );
 *
 * const isEnabled = flags?.['feature-flag-1'] ?? false;
 * ```
 */
export const useFlagCheck = (
  params: UseFlagChecksParams,
  options?: HookOptionsTypes
): FlagCheckResponse => {
  const { flags, context } = params;
  const { skip = false, ...rest } = options ?? {};

  const qst = qs.stringify(
    {
      flags,
      context,
    },
    { arrayFormat: 'brackets' }
  );

  const shouldSkip = skip || !context?.userId;

  return useSWRImmutable<UserFlags>(
    shouldSkip ? null : [`${FLAGS_URL}/v1/flags/check?${qst}`, context],
    ([url]) => fetcher()(url),
    rest
  );
};

export default useFlagCheck;
